export type SupportedLanguage = "en-us" | "en-ca" | "fr-ca";
export type SupportedCountry = "us" | "ca";
export type SupportedLocaleSegment = "en_US" | "en_CA" | "fr_CA";

const languageToRouteMap: Record<SupportedLanguage, { country: SupportedCountry; locale: SupportedLocaleSegment }> = {
    "en-us": { country: "us", locale: "en_US" },
    "en-ca": { country: "ca", locale: "en_CA" },
    "fr-ca": { country: "ca", locale: "fr_CA" },
};

const countryLocaleToLanguageMap: Record<string, SupportedLanguage> = {
    "us:en_US": "en-us",
    "ca:en_CA": "en-ca",
    "ca:fr_CA": "fr-ca",
};

const toSegments = (pathname: string): string[] => {
    return pathname.split("/").filter(Boolean);
};

export const isSupportedLanguage = (value: string): value is SupportedLanguage => {
    return value === "en-us" || value === "en-ca" || value === "fr-ca";
};

export const normalizeLanguage = (value: string): SupportedLanguage => {
    const normalizedValue = value.toLowerCase();
    return isSupportedLanguage(normalizedValue) ? normalizedValue : "en-us";
};

export const languageFromRoute = (
    country: string,
    locale: string,
): SupportedLanguage | null => {
    return countryLocaleToLanguageMap[`${country.toLowerCase()}:${locale}`] ?? null;
};

export const routeFromLanguage = (language: SupportedLanguage): { country: SupportedCountry; locale: SupportedLocaleSegment } => {
    return languageToRouteMap[language];
};

export const getLanguageFromPathname = (pathname: string): SupportedLanguage | null => {
    const segments = toSegments(pathname);
    if (segments[0] !== "welcome" || segments.length < 3) {
        return null;
    }

    return languageFromRoute(segments[1], segments[2]);
};

export const getInnerPathFromPathname = (pathname: string): string => {
    const segments = toSegments(pathname);
    if (segments.length === 0) {
        return "";
    }

    if (segments[0] === "welcome") {
        if (segments.length >= 3 && languageFromRoute(segments[1], segments[2])) {
            return segments.slice(3).join("/");
        }
        return segments.slice(1).join("/");
    }

    return segments.join("/");
};

export const buildPathFromLanguage = (language: SupportedLanguage, innerPath = ""): string => {
    const { country, locale } = routeFromLanguage(language);
    const trimmedInnerPath = innerPath.split("/").filter(Boolean).join("/");

    if (!trimmedInnerPath) {
        return `/welcome/${country}/${locale}`;
    }

    return `/welcome/${country}/${locale}/${trimmedInnerPath}`;
};

export const buildCanonicalWelcomePath = (pathname: string, language: SupportedLanguage): string => {
    const innerPath = getInnerPathFromPathname(pathname);
    return buildPathFromLanguage(language, innerPath);
};

export const languageFromGeoCountry = (geoCountry: string | null | undefined): SupportedLanguage => {
    if (!geoCountry) {
        return "en-us";
    }

    return geoCountry.toLowerCase() === "ca" ? "en-ca" : "en-us";
};